import {
  ActionIcon,
  Box,
  Group,
  Text,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import {
  IconCompass,
  IconHammer,
  IconLogout,
  IconMessageCircle,
  IconShieldCog,
  IconSofa,
  IconUser,
} from "@tabler/icons-react";
import type { ReactNode } from "react";
import type { Session } from "../lib/api";
import { ALPHA_BANNER_HEIGHT } from "./AlphaConstructionBanner";
import { BrandWordmark } from "./BrandWordmark";

export type AppTab =
  | "world"
  | "furniture"
  | "creator"
  | "profile"
  | "social"
  | "admin";

interface TabDef {
  id: AppTab;
  label: string;
  icon: typeof IconCompass;
}

const TABS: TabDef[] = [
  { id: "world", label: "World", icon: IconCompass },
  { id: "furniture", label: "Furniture", icon: IconSofa },
  { id: "creator", label: "Creator", icon: IconHammer },
  { id: "profile", label: "Profile", icon: IconUser },
  { id: "social", label: "Social", icon: IconMessageCircle },
  { id: "admin", label: "Admin", icon: IconShieldCog },
];

const NAV_HEIGHT = 56;
const MOBILE_NAV_HEIGHT = 64;

interface Props {
  session: Session;
  activeTab: AppTab;
  onTabChange: (tab: AppTab) => void;
  onDisconnect: () => void;
  showAdminTab?: boolean;
  showCreatorTab?: boolean;
  unreadSocialCount?: number;
  children: ReactNode;
}

function TabButton({
  tab,
  active,
  onClick,
  mobile,
  badge,
}: {
  tab: TabDef;
  active: boolean;
  onClick: () => void;
  mobile?: boolean;
  badge?: number;
}) {
  const Icon = tab.icon;
  return (
    <UnstyledButton
      onClick={onClick}
      style={{
        display: "flex",
        flexDirection: mobile ? "column" : "row",
        alignItems: "center",
        gap: mobile ? 2 : 8,
        padding: mobile ? "6px 16px" : "6px 16px",
        borderRadius: 10,
        background: active ? "rgba(253, 126, 20, 0.10)" : "transparent",
        color: active ? "#e8590c" : "#495057",
        transition: "all 0.15s ease",
        flex: mobile ? 1 : undefined,
        justifyContent: "center",
        position: "relative",
      }}
    >
      <Box style={{ position: "relative" }}>
        <Icon size={mobile ? 22 : 18} stroke={active ? 2 : 1.5} />
        {badge != null && badge > 0 && (
          <Box
            style={{
              position: "absolute",
              top: -6,
              right: -8,
              minWidth: 16,
              height: 16,
              borderRadius: 8,
              background: "#ff0000",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 10,
              color: "white",
              fontWeight: 700,
              lineHeight: 1,
              padding: "0 4px",
            }}
          >
            {badge > 99 ? "99+" : badge}
          </Box>
        )}
      </Box>
      <Text
        size={mobile ? "xs" : "sm"}
        fw={active ? 600 : 400}
        lh={1}
        style={{ letterSpacing: "0.02em" }}
      >
        {tab.label}
      </Text>
    </UnstyledButton>
  );
}

export function AppShell({
  session,
  activeTab,
  onTabChange,
  onDisconnect,
  showAdminTab = false,
  showCreatorTab = false,
  unreadSocialCount = 0,
  children,
}: Props) {
  let tabs = TABS;
  if (!showCreatorTab) {
    tabs = tabs.filter((tab) => tab.id !== "creator");
  }
  if (!showAdminTab) {
    tabs = tabs.filter((tab) => tab.id !== "admin");
  }

  return (
    <Box
      style={{
        background: "linear-gradient(180deg, #f0f2f5 0%, #fafbfc 100%)",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        boxSizing: "border-box",
        paddingTop: ALPHA_BANNER_HEIGHT,
      }}
    >
      {/* Desktop top nav */}
      <Box
        component="nav"
        style={{
          background: "#ffffff",
          borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06)",
          borderRadius: 0,
          height: NAV_HEIGHT,
          flexShrink: 0,
          position: "sticky",
          top: 0,
          zIndex: 100,
        }}
        visibleFrom="sm"
      >
        <Group
          h="100%"
          px="lg"
          justify="space-between"
          style={{ maxWidth: 1200, margin: "0 auto", width: "100%" }}
        >
          {/* Left — logo */}
          <Group gap="sm">
            <img
              src="/assets/drobbit.gif"
              alt="Drobbit"
              style={{ width: 28, height: 28, borderRadius: 6 }}
            />
            <Text
              fw={400}
              fz={22}
              c="dark"
              ff="'Bebas Neue', sans-serif"
              lts="0.04em"
            >
              <BrandWordmark />
            </Text>
          </Group>

          {/* Center — tabs */}
          <Group gap={4}>
            {tabs.map((tab) => (
              <TabButton
                key={tab.id}
                tab={tab}
                active={activeTab === tab.id}
                onClick={() => onTabChange(tab.id)}
                badge={tab.id === "social" ? unreadSocialCount : undefined}
              />
            ))}
          </Group>

          {/* Right — user + logout */}
          <Group gap="sm">
            <Box
              w={8}
              h={8}
              style={{
                borderRadius: "50%",
                background: session.avatarColor || "#ff922b",
                flexShrink: 0,
              }}
            />
            <Text size="sm" c="gray.7" fw={500}>
              {session.displayName}
            </Text>
            <Tooltip label="Disconnect" position="bottom" withArrow>
              <ActionIcon
                variant="subtle"
                size="sm"
                color="gray.7"
                onClick={onDisconnect}
              >
                <IconLogout size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </Box>

      {/* Content area */}
      <Box style={{ flex: 1, minHeight: 0, overflow: "auto" }}>{children}</Box>

      {/* Mobile bottom nav */}
      <Box
        component="nav"
        hiddenFrom="sm"
        style={{
          background: "#ffffff",
          borderTop: "1px solid rgba(0, 0, 0, 0.08)",
          boxShadow: "0 -1px 3px rgba(0, 0, 0, 0.06)",
          borderRadius: 0,
          height: MOBILE_NAV_HEIGHT,
          flexShrink: 0,
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }}
      >
        <Group h="100%" justify="space-around" align="center" px="xs">
          {tabs.map((tab) => (
            <TabButton
              key={tab.id}
              tab={tab}
              active={activeTab === tab.id}
              onClick={() => onTabChange(tab.id)}
              mobile
              badge={tab.id === "social" ? unreadSocialCount : undefined}
            />
          ))}
        </Group>
      </Box>
    </Box>
  );
}
